import React, { ReactNode } from 'react';
import { AutoComplete, Divider, Form } from 'antd';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components/macro';
import { Secret } from '../../../../../../types.generated';
import CreateSecretButton from './CreateSecretButton';
import { RecipeField } from '../common';
import { ANTD_GRAY } from '../../../../../entity/shared/constants';
import { clearSecretListCache } from '../../../../secret/cacheUtils';
import { useTranslation } from '../../../../../../i18n/useTranslation';
import { getTranslatedPlaceholder, translateRulesMessage } from '../../../utils';

const StyledDivider = styled(Divider)`
    margin: 0;
`;

export const StyledFormItem = styled(Form.Item)<{
    alignLeft?: boolean;
    removeMargin?: boolean;
    isSecretField?: boolean;
}>`
    margin-bottom: ${(props) => (props.removeMargin ? '0' : '16px')};

    ${(props) =>
        props.alignLeft &&
        `
        .ant-form-item {
            flex-direction: row;

        }

        .ant-form-item-label {
            padding: 0;
            margin-right: 10px;
        }
    `}

    ${(props) =>
        props.isSecretField &&
        `
        .ant-form-item-label {
            &:after {
                content: 'Secret Field';
                color: ${ANTD_GRAY[7]};
                font-style: italic;
                font-weight: 100;
                margin-left: 5px;
                font-size: 14px;
            }
        }
    `}

    &.f-checkbox-wrapper {
        .ant-form-item-row {
            gap: 8px;
            align-items: center;
            justify-content: flex-end;
            flex-direction: row-reverse;
        }

        .ant-form-item-control {
            flex-grow: 0;
            width: fit-content;

            .ant-form-item-control-input {
                min-height: auto;
            }
        }
    }
`;

interface SecretFieldProps {
    field: RecipeField;
    secrets: Secret[];
    removeMargin?: boolean;
    refetchSecrets: () => void;
    updateFormValue: (field, value) => void;
}

function SecretFieldTooltip({ tooltipLabel }: { tooltipLabel?: string | ReactNode }) {
    const { t } = useTranslation();
    return (
        <div>
            {tooltipLabel && (
                <>
                    {t(tooltipLabel as string)}
                    <hr />
                </>
            )}
            <p>
                {/* Removed the anchor tag for now */}
                {t(
                    'This field requires you to use a Data Catalog Secret. For more information on Secrets in Data Catalog, please review',
                )}
                <span>{t('the docs')}</span>.
            </p>
        </div>
    );
}

const encodeSecret = (secretName: string) => {
    return `\${${secretName}}`;
};

function SecretField({ field, secrets, removeMargin, updateFormValue, refetchSecrets }: SecretFieldProps) {
    const { t } = useTranslation();
    const options = secrets.map((secret) => ({ value: encodeSecret(secret.name), label: secret.name, tabIndex: 0 }));
    const apolloClient = useApolloClient();

    return (
        <StyledFormItem
            required={field.required}
            name={field.name}
            label={t(field.label)}
            rules={translateRulesMessage(field.rules || [], t(field.label))}
            tooltip={<SecretFieldTooltip tooltipLabel={field?.tooltip} />}
            removeMargin={!!removeMargin}
            isSecretField
        >
            <AutoComplete
                placeholder={getTranslatedPlaceholder(field.placeholder || '')}
                aria-label={t(field.label || '')}
                filterOption={(input, option) => !!option?.value.toLowerCase().includes(input.toLowerCase())}
                notFoundContent={<>{t('No secrets found')}</>}
                tabIndex={0}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                options={options}
                dropdownRender={(menu) => {
                    return (
                        <>
                            {menu}
                            <StyledDivider />
                            <CreateSecretButton
                                onSubmit={(state) => {
                                    updateFormValue(field.name, encodeSecret(state.name as string));
                                    setTimeout(() => clearSecretListCache(apolloClient), 3000);
                                }}
                                refetchSecrets={refetchSecrets}
                            />
                        </>
                    );
                }}
            />
        </StyledFormItem>
    );
}

export default SecretField;
